import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import logger from '../../utils/logger';

const ErrorContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: '#f5f5f5',
  borderRadius: 8,
  maxWidth: 600,
  margin: '0 auto',
}));

const ErrorTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
  fontWeight: 600,
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
}));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to our logging service
    logger.error('ErrorBoundary', 'Component error caught', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'Unknown'
    });

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Report to error tracking service if available
    if (window.gtag && process.env.NODE_ENV === 'production') {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // If provided, call the onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo,
          this.handleReset
        );
      }

      // Default fallback UI
      return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <ErrorContainer elevation={0}>
            <ErrorTitle variant="h5">
              Oops! Something went wrong
            </ErrorTitle>
            
            <ErrorMessage variant="body1">
              We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
            </ErrorMessage>

            {/* Show technical details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: '#fff',
                borderRadius: 1,
                textAlign: 'left',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 300,
                overflow: 'auto',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Error Details:
                </Typography>
                {this.state.error.toString()}
                {this.state.errorInfo && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 2, mb: 1 }}>
                      Component Stack:
                    </Typography>
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={this.handleReset}
                sx={{ 
                  backgroundColor: '#121212',
                  '&:hover': { backgroundColor: '#333' }
                }}
              >
                Try Again
              </Button>
              
              <Button 
                variant="outlined"
                onClick={this.handleReload}
                sx={{ 
                  borderColor: '#121212',
                  color: '#121212',
                  '&:hover': { 
                    borderColor: '#333',
                    backgroundColor: 'rgba(18, 18, 18, 0.04)'
                  }
                }}
              >
                Refresh Page
              </Button>
            </Box>

            {this.state.errorCount > 2 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                Multiple errors detected. Please refresh the page if issues persist.
              </Typography>
            )}
          </ErrorContainer>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;